import React from 'react';
import { Card, Descriptions, Spin, Empty, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getApartmentUnitById } from '@/api/properties';
import { useAuth } from '@/contexts/AuthContext';


interface ApartmentUnitTabProps {
  apartmentUnitId: string;
}

const ApartmentUnitTab: React.FC<ApartmentUnitTabProps> = ({ apartmentUnitId }) => {
  const { t } = useTranslation(['properties', 'common']);
  const { firestoreUser } = useAuth();

  const { data: apartmentUnit, isLoading, error } = useQuery({
    queryKey: ['apartmentUnit', apartmentUnitId],
    queryFn: () => getApartmentUnitById(apartmentUnitId, firestoreUser!),
    enabled: !!apartmentUnitId && !!firestoreUser,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <Card>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (error || !apartmentUnit) {
    return (
      <Card>
        <Empty description={t('properties:apartmentUnit.notFound')} />
      </Card>
    );
  }

  return (
    <Card>
      <Descriptions bordered column={1}>
        <Descriptions.Item label={t('properties:apartmentUnit.fields.number')}>
          {apartmentUnit.number}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('properties:apartmentUnit.fields.status')}>
          <Tag color={apartmentUnit.status === 'occupied' ? 'red' : apartmentUnit.status === 'vacant' ? 'green' : 'default'}>
            {t(`properties:apartmentUnit.statuses.${apartmentUnit.status}`)}
          </Tag>
        </Descriptions.Item>
        
        <Descriptions.Item label={t('properties:apartmentUnit.fields.bedrooms')}>
          {apartmentUnit.bedrooms}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('properties:apartmentUnit.fields.bathrooms')}>
          {apartmentUnit.bathrooms}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('properties:apartmentUnit.fields.area')}>
          {apartmentUnit.area} sq ft
        </Descriptions.Item>
        
        <Descriptions.Item label={t('properties:apartmentUnit.fields.description')}>
          {apartmentUnit.description || '-'}
        </Descriptions.Item>
        
        {apartmentUnit.block && (
          <Descriptions.Item label={t('properties:apartmentUnit.fields.block')}>
            {apartmentUnit.block.name}
          </Descriptions.Item>
        )}
        
        {apartmentUnit.floor && (
          <Descriptions.Item label={t('properties:apartmentUnit.fields.floor')}>
            {apartmentUnit.floor.name}
          </Descriptions.Item>
        )}
        
        {apartmentUnit.resident && (
          <Descriptions.Item label={t('properties:apartmentUnit.fields.resident')}>
            {apartmentUnit.resident.name}
          </Descriptions.Item>
        )}
      </Descriptions>
    </Card>
  );
};

export default ApartmentUnitTab; 